import React, { useEffect, useReducer, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CosmeticRepairRequest from './CosmeticRepairRequest';
import CosmeticRepairRequestReducer from './CosmeticRepairRequestReducer';
import {
    CosmeticPageErrors,
    CosmeticPageState,
    CosmeticProcessStages,
    DamageTypes,
    ReducerActionNames,
    Panels,
    CosmeticRepairRequestType
} from './CosmeticRepairRequestTypes';
import { CommonValidation } from '../../common/commonValidation/commonValidation';
import { SelectInputHelper } from '../../common/selectInput/SelectInput';
import {
    Locations,
    ProductTypes,
    RoutePaths,
    UploadImageTypes
} from '../../../Constants/Common';
import { PolicyHolderApi } from '../../../api/PolicyHolderApi';
import { AddressType, PolicyHolderType } from '../../common/CommonTypes';
import { RepairRequestApi } from '../../../api/RepairRequestApi';
import { useAuth0 } from '@auth0/auth0-react';
import ProgressBar, {
    ProgressBarItem
} from '../../common/progressBar/ProgressBar';
import { ClientDateHandler } from '../../common/clientDateHandler/ClientDateHandler';
import ReloadHandler from '../../common/ReloadHandler';
import { useSelectedProduct } from '../../hooks/useSelectedProduct';
import { useSelectedVehicle } from '../../hooks/useSelectedVehicle';
import { GoogleAnalytics } from '../../../classes/GoogleAnalytics';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

const CosmeticRepairRequestManager = (): JSX.Element => {
    const { user, getAccessTokenSilently } = useAuth0();

    const defaultState: CosmeticPageState = {
        cosmeticStage: CosmeticProcessStages.General,
        cosmeticRepairRequests: [],
        currentRepairRequest: undefined,
        cosmeticFormError: undefined,
        hasErrors: false,
        repairRequestSelectedIndex: -1,
        repairAddressSelectedIndex: -1,
        repairAddresses: [],
        scrollToAddress: false,
        showRepairRequestForm: false,
        isLoadingOnSubmission: false,
        permissionGranted: false,
        selectedPanels: [],
        showCancelAlert: false
    };

    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const isSiteLocationUs = i18n.language?.toLowerCase() === Locations.Us;

    const distanceImageMsg =
        i18next.language.toLocaleLowerCase() === 'us'
            ? t('image_from_3feet_away_is_required')
            : t('image_from_1m_away_is_required');

    const [state, dispatch] = useReducer(
        CosmeticRepairRequestReducer,
        defaultState
    );

    const vehicle = useSelectedVehicle();
    const { product } = useSelectedProduct();

    useEffect(() => {
        const getPolicyHolderDetails = async () => {
            const token = await getAccessTokenSilently();

            new PolicyHolderApi().getPolicyHolderDetails(
                product.reference,
                token,
                onGetPolicyHolderSuccess,
                onGetPolicyHolderFailure
            );
        };

        if (!isSiteLocationUs) {
            getPolicyHolderDetails();
        }
    }, []);

    const onGetPolicyHolderSuccess = (
        policyHolderDetails: PolicyHolderType
    ) => {
        dispatch({
            type: ReducerActionNames.SetRepairAddress,
            address: policyHolderDetails.address
        });
    };

    const onGetPolicyHolderFailure = (error: string) => {
        console.log(`Failed to get the policy holder details: ${error}`);
    };

    const onChange = (key: string, value: string) => {
        dispatch({
            type: ReducerActionNames.UpdateCosmeticDate,
            key,
            value
        });
    };

    const {
        cosmeticStage,
        cosmeticRepairRequests,
        currentRepairRequest,
        cosmeticFormError,
        repairAddressSelectedIndex,
        repairAddresses,
        scrollToAddress,
        showRepairRequestForm,
        isLoadingOnSubmission,
        permissionGranted,
        showCancelAlert
    } = state;
    const progressBarItems: ProgressBarItem[] = [
        {
            order: 1,
            text: t('add_repair_requests'),
            active: cosmeticStage === CosmeticProcessStages.General,
            disabled: false,
            visible: true
        },
        {
            order: 2,
            text: t('confirm_repairer_location'),
            active: cosmeticStage === CosmeticProcessStages.RepairAddress,
            disabled: false,
            visible: true
        },
        {
            order: 3,
            text: t('summary'),
            active: cosmeticStage === CosmeticProcessStages.Summary,
            disabled: false,
            visible: true
        },
        {
            order: 4,
            text: t('confirmation'),
            active: false,
            disabled: false,
            visible: true
        }
    ];
    const onSaveCosmetic = (): boolean => {
        const formWasValid = validateCurrentCosmeticRepairRequest();
        const rowIndex = cosmeticRepairRequests.findIndex(
            (row) => row.id === currentRepairRequest.id
        );
        if (
            rowIndex != -1 &&
            cosmeticRepairRequests[rowIndex].dateOfIncident !=
                currentRepairRequest.dateOfIncident
        )
            GoogleAnalytics.trackEventsGoogleAnalytics(
                'Button click',
                'Cosmetic request click',
                'Clicked change the date of incident'
            );

        if (formWasValid) {
            dispatch({
                type: ReducerActionNames.SaveCosmeticRequest
            });
        } else {
            dispatch({
                type: ReducerActionNames.SetHasErrors,
                hasErrors: !formWasValid
            });
        }
        return formWasValid;
    };
    const onAddCosmeticRequest = () => {
        dispatch({
            type: ReducerActionNames.AddCosmeticRequest,
            productReference: product.reference
        });
    };
    const onChangeDamageType = (selectionName: string) => {
        dispatch({
            type: ReducerActionNames.UpdateDamageType,
            key: 'damageType',
            value: selectionName
        });
    };

    useEffect(() => {
        if (!showDamageDepthQuestion()) {
            if (
                CommonValidation.isNotEmpty(
                    cosmeticFormError?.cosmeticGeneral?.damageDepth
                )
            ) {
                let errors = cosmeticFormError;

                errors = {
                    ...errors,
                    cosmeticGeneral: {
                        ...errors.cosmeticGeneral,
                        damageDepth: undefined
                    }
                };

                dispatch({
                    type: ReducerActionNames.UpdateCosmeticErrors,
                    cosmeticErrors: errors
                });
            }

            dispatch({
                type: ReducerActionNames.UpdateDamageDepth,
                damageDepth: SelectInputHelper.NullValue()
            });
        }
    }, [currentRepairRequest?.damageType]);

    const onChangeMileage = (event: React.FormEvent<HTMLInputElement>) => {
        dispatch({
            type: ReducerActionNames.UpdateMileage,
            mileage: event.currentTarget.value
        });
    };

    const onChangeDamageDepth = (selectedValue: string) => {
        dispatch({
            type: ReducerActionNames.UpdateDamageDepth,
            damageDepth: selectedValue
        });
    };

    const showDamageDepthQuestion = () => {
        const result =
            currentRepairRequest &&
            CommonValidation.isNotEmpty(currentRepairRequest.damageType) &&
            i18n.language.toLowerCase() !== Locations.Us &&
            (currentRepairRequest.damageType == DamageTypes.DentNoPaintDamage ||
                currentRepairRequest.damageType == DamageTypes.DentPaintDamage);
        return result;
    };

    const onUploadImageChange = (
        imageType: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => {
        const uploadedImages = currentRepairRequest.uploadedImages;

        const uploadedImage = uploadedImages.find((ui) => ui.type == imageType);

        if (uploadedImage) {
            uploadedImage.uploadedPath = uploadedPath;
            uploadedImage.image = image;
            uploadedImage.filename = filename;
            uploadedImage.imageId = imageId;
            dispatch({
                type: ReducerActionNames.UpdateUploadedImages,
                uploadedImages: uploadedImages
            });
        }
    };

    const onSelectedPanelChanged = (selected: boolean, panel: Panels) => {
        dispatch({
            type: ReducerActionNames.UpdateSelectedPanels,
            selected,
            panel
        });
    };

    const validateCurrentCosmeticRepairRequest = () => {
        let errors: CosmeticPageErrors = {};
        let isValid = true;
        if (
            CommonValidation.isDateNull(
                currentRepairRequest.dateOfIncident,
                true
            ) ||
            CommonValidation.isEmpty(currentRepairRequest.dateOfIncident)
        ) {
            isValid = false;
            errors = {
                ...errors,
                cosmeticGeneral: {
                    ...errors.cosmeticGeneral,
                    dateOfIncident: t('date_of_incident_is_required')
                }
            };
        } else if (
            !CommonValidation.isValidDate(
                currentRepairRequest.dateOfIncident,
                true
            )
        ) {
            isValid = false;
            errors = {
                ...errors,
                cosmeticGeneral: {
                    ...errors.cosmeticGeneral,
                    dateOfIncident: t(
                        'date_of_incident_is_out_of_a_valid_range'
                    )
                }
            };
        }
        if (CommonValidation.isEmpty(currentRepairRequest.mileage)) {
            isValid = false;
            errors = {
                ...errors,
                cosmeticGeneral: {
                    ...errors.cosmeticGeneral,
                    mileage: t('approximate_mileage_is_required')
                }
            };
        } else if (!CommonValidation.isInteger(currentRepairRequest.mileage)) {
            isValid = false;
            errors = {
                ...errors,
                cosmeticGeneral: {
                    ...errors.cosmeticGeneral,
                    mileage: t('approx_mileage_is_number')
                }
            };
        }

        if (
            !currentRepairRequest.selectedPanels ||
            currentRepairRequest.selectedPanels.length === 0
        ) {
            isValid = false;
            errors = {
                ...errors,
                cosmeticGeneral: {
                    ...errors.cosmeticGeneral,
                    selectedPanels: t('please_select_at_least_one_panel')
                }
            };
            GoogleAnalytics.trackEventsGoogleAnalytics(
                'Button click',
                'Cosmetic request click',
                'Clicked continue without selecting a panel'
            );
        }

        if (CommonValidation.isEmpty(currentRepairRequest.incidentOccured)) {
            isValid = false;
            errors = {
                ...errors,
                cosmeticGeneral: {
                    ...errors.cosmeticGeneral,
                    incidentOccured: t('please_provide_additional_information')
                }
            };
            GoogleAnalytics.trackEventsGoogleAnalytics(
                'Button click',
                'Cosmetic request click',
                'Clicked continue without providing information on how the damage occurred'
            );
        }
        if (SelectInputHelper.isNullValue(currentRepairRequest.damageType)) {
            isValid = false;
            errors = {
                ...errors,
                cosmeticGeneral: {
                    ...errors.cosmeticGeneral,
                    damageType: t('type_of_damage_is_required')
                }
            };
            GoogleAnalytics.trackEventsGoogleAnalytics(
                'Button click',
                'Cosmetic request click',
                'Clicked continue without selecting a damage type'
            );
        }
        if (
            showDamageDepthQuestion() &&
            SelectInputHelper.isNullValue(currentRepairRequest.damageDepth)
        ) {
            isValid = false;
            errors = {
                ...errors,
                cosmeticGeneral: {
                    ...errors.cosmeticGeneral,
                    damageDepth: t(
                        'damage_deeper_than_the_depth_of_a_pound_coin_is_required'
                    )
                }
            };
        }
        if (SelectInputHelper.isNullValue(currentRepairRequest.damageLength)) {
            isValid = false;
            errors = {
                ...errors,
                cosmeticGeneral: {
                    ...errors.cosmeticGeneral,
                    damageLength: t('indicate_the_length_of_damage_is_required')
                }
            };
        }

        if (
            CommonValidation.isEmpty(
                currentRepairRequest.uploadedImages.find(
                    (ui) => ui.type == UploadImageTypes.CloseUp
                )?.uploadedPath
            )
        ) {
            isValid = false;
            errors = {
                ...errors,
                cosmeticGeneral: {
                    ...errors.cosmeticGeneral,
                    closeUpImage: t('image_of_the_measured_damage_is_required')
                }
            };
        }

        if (
            CommonValidation.isEmpty(
                currentRepairRequest.uploadedImages.find(
                    (ui) => ui.type == UploadImageTypes.Distance
                )?.uploadedPath
            )
        ) {
            isValid = false;
            errors = {
                ...errors,
                cosmeticGeneral: {
                    ...errors.cosmeticGeneral,
                    distanceImage: distanceImageMsg
                }
            };
        }

        dispatch({
            type: ReducerActionNames.UpdateCosmeticErrors,
            cosmeticErrors: errors
        });

        return isValid;
    };

    const resetGeneralErrors = () => {
        const errors: CosmeticPageErrors = {};

        dispatch({
            type: ReducerActionNames.UpdateCosmeticErrors,
            cosmeticErrors: errors
        });
    };

    const onContinueToRepairAddress = () => {
        let errors: CosmeticPageErrors = {};
        if (cosmeticRepairRequests && cosmeticRepairRequests.length > 0) {
            dispatch({
                type: ReducerActionNames.UpdateCosmeticStage,
                newStage: CosmeticProcessStages.RepairAddress
            });
        } else {
            errors = {
                ...errors,
                cosmeticGeneral: {
                    ...errors.cosmeticGeneral,
                    noCosmeticRequests: t('no_repair_requests_text')
                }
            };
        }

        dispatch({
            type: ReducerActionNames.UpdateCosmeticErrors,
            cosmeticErrors: errors
        });
    };
    const onContinueToCosmeticSummary = () => {
        if (validateCosmeticAddressPage()) {
            dispatch({
                type: ReducerActionNames.UpdateCosmeticStage,
                newStage: CosmeticProcessStages.Summary
            });
        }
    };
    const onUpdateAddressClickFromSummary = (scrollToAddress): void => {
        dispatch({
            type: ReducerActionNames.ScrollToAddress,
            scrollToAddress: scrollToAddress
        });
    };
    const onChangeStage = (stage: CosmeticProcessStages): void => {
        dispatch({
            type: ReducerActionNames.UpdateCosmeticStage,
            newStage: stage
        });
        if (scrollToAddress) {
            onUpdateAddressClickFromSummary(false);
        }
    };

    const onRepairAddressSelectionChanged = (index: number) => {
        dispatch({
            type: ReducerActionNames.UpdateRepairAddressSelected,
            value: index
        });
    };

    const onSubmitCosmeticRepairRequests = () => {
        const validPage = validateCosmeticSubmitRequest();

        if (validPage) {
            saveRepairRequest();
        }
    };

    const saveRepairRequest = async () => {
        const successCallback = (repairRequests) => {
            const activeConfirmationItems = progressBarItems.map((p) =>
                p.text === 'Confirmation'
                    ? { ...p, active: true }
                    : { ...p, active: false }
            );

            navigate(RoutePaths.Confirmation, {
                state: {
                    progressBarItems: activeConfirmationItems,
                    repairRequests,
                    productType: ProductTypes.Cosmetic
                }
            });
        };

        const failureCallback = (error) => {
            console.log(
                `Failed to submit the cosmetic repair request: ${error}`
            );

            navigate(RoutePaths.ConfirmationError);
        };

        dispatch({
            type: ReducerActionNames.LoadingOnRequestSubmission,
            loadingOnRequestSubmission: true
        });

        const repairRequests = cosmeticRepairRequests.map((request) =>
            getRepairRequest(request)
        );

        console.log(`Repair requests: ${JSON.stringify(repairRequests)}`);

        const token = await getAccessTokenSilently();

        new RepairRequestApi().saveCosmetic(
            repairRequests,
            token,
            successCallback,
            failureCallback
        );
    };

    const getRepairRequest = (request: CosmeticRepairRequestType) => {
        console.log('request', request);
        const repairRequest = {
            submitterEmailAddress: user.email,
            productReference: request.productReference,
            dateOfIncident: mapJsonDateToServer(request.dateOfIncident),
            mileage: request.mileage,
            incidentOccured: request.incidentOccured,
            damageType: request.damageType,
            damageDepth:
                request.damageDepth === SelectInputHelper.NullValue()
                    ? null
                    : request.damageDepth,
            damageLength: request.damageLength,
            closeUpImageGuid:
                request.uploadedImages.find(
                    (ui) => ui.type === UploadImageTypes.CloseUp
                ).imageId || '00000000-0000-0000-0000-000000000000',
            distanceImageGuid:
                request.uploadedImages.find(
                    (ui) => ui.type === UploadImageTypes.Distance
                ).imageId || '00000000-0000-0000-0000-000000000000',
            additionalImage1Guid:
                request.uploadedImages.find(
                    (ui) => ui.type === UploadImageTypes.AdditionalImage1
                ).imageId || '00000000-0000-0000-0000-000000000000',
            additionalImage2Guid:
                request.uploadedImages.find(
                    (ui) => ui.type === UploadImageTypes.AdditionalImage2
                ).imageId || '00000000-0000-0000-0000-000000000000',
            selectedPanels: request.selectedPanels,
            repairAddress: repairAddresses[repairAddressSelectedIndex]
        };
        console.log(repairRequest);
        return repairRequest;
    };

    const mapJsonDateToServer = (dateJson: string) => {
        const cdh = new ClientDateHandler();
        cdh.parseJSON(dateJson);

        const localDate = cdh.getLocalDate();
        return localDate;
    };

    const validateCosmeticSubmitRequest = () => {
        let isValid = true;
        let errors: CosmeticPageErrors = {};

        if (!permissionGranted) {
            isValid = false;
            errors = {
                ...errors,
                permissionGranted: t('validate_agreement')
            };
        }

        dispatch({
            type: ReducerActionNames.UpdateCosmeticErrors,
            cosmeticErrors: errors
        });

        return isValid;
    };
    const validateCosmeticAddressPage = () => {
        let isValid = true;
        let errors: CosmeticPageErrors = {};

        if (repairAddressSelectedIndex < 0) {
            isValid = false;
            errors = {
                ...errors,
                cosmeticAddress: {
                    ...errors.cosmeticAddress,
                    noRepairAddressSelected: t('select_repair_address')
                }
            };
            GoogleAnalytics.trackEventsGoogleAnalytics(
                'Button click',
                'Cosmetic request click',
                'Clicked continue without selecting an address'
            );
        }

        dispatch({
            type: ReducerActionNames.UpdateCosmeticErrors,
            cosmeticErrors: errors
        });

        return isValid;
    };

    const onUpdateCosmeticRequest = (requestId: number) => {
        dispatch({
            type: ReducerActionNames.UpdateCosmeticRequest,
            requestId: requestId,
            productReference: product.reference
        });
    };

    const onDeleteCosmeticRequest = (requestId: number) => {
        dispatch({
            type: ReducerActionNames.DeleteCosmeticRequest,
            requestId: requestId
        });
    };

    const onDefaultDealerAddress = (address: AddressType) => {
        dispatch({
            type: ReducerActionNames.SetRepairAddress,
            address: address
        });
    };

    const onShowRepairRequestForm = (show: boolean) => {
        dispatch({
            type: ReducerActionNames.ShowRepairRequestForm,
            show
        });
    };

    const onTogglePermission = (isChecked: boolean) => {
        dispatch({
            type: ReducerActionNames.SetPermissionGranted,
            permissionGranted: isChecked
        });
    };
    const onCancelClick = () => {
        dispatch({
            type: ReducerActionNames.ShowCancelAlert,
            showCancelAlert: true
        });
    };
    const onCancelRepairRequest = () => {
        GoogleAnalytics.trackEventsGoogleAnalytics(
            'Button click',
            'Cosmetic request click',
            'Clicked cancel repair request'
        );
        navigate(RoutePaths.Home);
    };
    const onCancelUndo = () => {
        dispatch({
            type: ReducerActionNames.ShowCancelAlert,
            showCancelAlert: false
        });
    };
    const scrollDivRef = useRef<null | HTMLDivElement>(null);
    useEffect(() => {
        if (!scrollToAddress) scrollDivRef.current?.scrollIntoView();
    }, [cosmeticStage]);
    return (
        <>
            <div ref={scrollDivRef}></div>
            <ProgressBar
                id='cosmeticProgressBar'
                progressBarItems={progressBarItems}
            />
            <CosmeticRepairRequest
                onAddCosmeticRequest={onAddCosmeticRequest}
                cosmeticRequests={cosmeticRepairRequests}
                currentCosmeticRepairRequest={currentRepairRequest}
                cosmeticFormError={cosmeticFormError}
                cosmeticProcessStage={cosmeticStage}
                repairAddressSelectedIndex={repairAddressSelectedIndex}
                repairAddresses={repairAddresses}
                loadingOnSubmitRequests={isLoadingOnSubmission}
                showCancelAlert={showCancelAlert}
                onChange={onChange}
                onSaveCosmetic={onSaveCosmetic}
                resetGeneralErrors={resetGeneralErrors}
                onChangeMileage={onChangeMileage}
                onChangeDamageType={onChangeDamageType}
                onChangeDamageDepth={onChangeDamageDepth}
                onContinueToRepairAddress={onContinueToRepairAddress}
                showDamageDepthQuestion={showDamageDepthQuestion}
                onUploadImageChange={onUploadImageChange}
                onRepairAddressSelectionChanged={
                    onRepairAddressSelectionChanged
                }
                onSubmitCosmeticRepairRequests={onSubmitCosmeticRepairRequests}
                onUpdateCosmeticRequest={onUpdateCosmeticRequest}
                onSelectedPanelChanged={onSelectedPanelChanged}
                onContinueToCosmeticSummary={onContinueToCosmeticSummary}
                scrollToAddress={scrollToAddress}
                onUpdateAddressClickFromSummary={
                    onUpdateAddressClickFromSummary
                }
                onChangeStage={onChangeStage}
                onUpdateCosmeticClickFromSummary={onUpdateCosmeticRequest}
                onShowRepairRequestForm={onShowRepairRequestForm}
                showRepairRequestForm={showRepairRequestForm}
                permissionGranted={permissionGranted}
                vehicle={vehicle}
                onTogglePermission={onTogglePermission}
                onCancelClick={onCancelClick}
                onCancelRepairRequest={onCancelRepairRequest}
                onCancelUndo={onCancelUndo}
                onDeleteCosmeticRequest={onDeleteCosmeticRequest}
                onDefaultDealerAddress={onDefaultDealerAddress}
            />
            <ReloadHandler reloadPath={RoutePaths.LandingPage} />
        </>
    );
};

export default CosmeticRepairRequestManager;
